import React, { useEffect, useMemo, useState } from 'react';
import SubmitButton from '../../../../../components/forms/formComponents/SubmitButton';

function toDate(value) {
  const date = value?.toDate?.() || value || null;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

function plainBody(reply = {}) {
  const text = String(reply.text || '').trim();
  if (text) return text;
  return String(reply.html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

function replyName(reply = {}, recipientNameById = {}) {
  if (reply.direction === 'outbound') return reply.senderName || 'Admin';
  const fromName = String(reply.fromName || '').trim();
  const safeFromName = fromName.includes('@') ? '' : fromName;
  return reply.recipientName
    || (reply.recipientId ? recipientNameById[reply.recipientId] : '')
    || safeFromName
    || 'Member';
}

function replyTimestamp(reply = {}) {
  return toDate(reply.createdAt || reply.receivedAt || reply.sentAt)?.getTime() || 0;
}

function formatTimestamp(value) {
  const date = toDate(value);
  return date ? date.toLocaleString() : '';
}

function replyKey(reply = {}) {
  if (reply.recipientId) return `recipient:${reply.recipientId}`;
  const email = reply.direction === 'outbound' ? reply.toEmail : reply.fromEmail;
  if (email) return `email:${String(email).toLowerCase()}`;
  return `reply:${reply.id || Math.random()}`;
}

export function splitReplyBody(body = '') {
  const lines = String(body || '').replace(/\r\n/g, '\n').split('\n');
  const quoteStart = lines.findIndex((line) => (
    /^\s*>/.test(line) ||
    /^\s*On .+ wrote:\s*$/i.test(line) ||
    /^\s*From:\s+/i.test(line)
  ));

  if (quoteStart === -1) return { body: String(body || '').trim(), quoted: '' };
  const main = lines.slice(0, quoteStart).join('\n').trim();
  const quoted = lines.slice(quoteStart).join('\n').trim();
  return { body: main || String(body || '').trim(), quoted: main ? quoted : '' };
}

export function buildReplyConversations(replies = []) {
  const sorted = [...replies].sort((left, right) => replyTimestamp(left) - replyTimestamp(right));
  const conversations = [];
  const conversationsByKey = new Map();
  const keyByInboundId = new Map();

  sorted.forEach((reply) => {
    let key = replyKey(reply);
    if (reply.direction === 'outbound' && reply.inReplyToReplyId && keyByInboundId.has(reply.inReplyToReplyId)) {
      key = keyByInboundId.get(reply.inReplyToReplyId);
    }

    let conversation = conversationsByKey.get(key);
    if (!conversation) {
      conversation = { id: key, messages: [] };
      conversationsByKey.set(key, conversation);
      conversations.push(conversation);
    }

    conversation.messages.push(reply);
    if (reply.direction !== 'outbound' && reply.id) keyByInboundId.set(reply.id, key);
  });

  return conversations;
}

function inboundMessages(conversation = {}) {
  return (conversation.messages || []).filter((message) => message.direction !== 'outbound');
}

function latestMessage(conversation = {}) {
  return [...(conversation.messages || [])].sort((left, right) => replyTimestamp(right) - replyTimestamp(left))[0] || null;
}

function conversationTitle(conversation = {}, recipientNameById = {}) {
  const inbound = inboundMessages(conversation)[0];
  if (inbound) return replyName(inbound, recipientNameById);
  const latest = latestMessage(conversation);
  return latest ? replyName(latest, recipientNameById) : 'Conversation';
}

function conversationPreview(conversation = {}) {
  const latest = latestMessage(conversation);
  if (!latest) return '';
  const { body } = splitReplyBody(plainBody(latest));
  return body || latest.subject || '';
}

function conversationStorageKey({ teamId = '', campaignId = '', currentUserId = '' }) {
  if (!teamId || !campaignId || !currentUserId || typeof window === 'undefined') return '';
  return `memberNest.emailReplies.seen.${currentUserId}.${teamId}.${campaignId}`;
}

function readSeenInboundIds(key = '') {
  if (!key || typeof window === 'undefined') return { initialized: true, ids: new Set() };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { initialized: false, ids: new Set() };
    const parsed = JSON.parse(raw);
    return {
      initialized: true,
      ids: new Set(Array.isArray(parsed?.inboundReplyIds) ? parsed.inboundReplyIds : []),
    };
  } catch (err) {
    return { initialized: false, ids: new Set() };
  }
}

function writeSeenInboundIds(key = '', ids = []) {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify({
      inboundReplyIds: Array.from(new Set(ids)).filter(Boolean),
      updatedAt: new Date().toISOString(),
    }));
  } catch (err) {}
}

export function buildConversationViews(conversations = [], seenInboundIds = new Set(), recipientNameById = {}) {
  return conversations
    .map((conversation) => {
      const inbound = inboundMessages(conversation);
      const latest = latestMessage(conversation);
      const unreadInboundIds = inbound
        .map((message) => message.id)
        .filter((id) => id && !seenInboundIds.has(id));
      const outboundCount = (conversation.messages || []).filter((message) => message.direction === 'outbound').length;
      return {
        ...conversation,
        inboundCount: inbound.length,
        outboundCount,
        latestAt: replyTimestamp(latest),
        latest,
        title: conversationTitle(conversation, recipientNameById),
        preview: conversationPreview(conversation),
        unreadInboundIds,
        unreadCount: unreadInboundIds.length,
      };
    })
    .sort((left, right) => {
      if (left.unreadCount && !right.unreadCount) return -1;
      if (!left.unreadCount && right.unreadCount) return 1;
      return right.latestAt - left.latestAt;
    });
}

export function buildReplyThreads(replies = []) {
  const conversations = buildReplyConversations(replies);
  const threads = [];
  const orphanReplies = [];

  conversations.forEach((conversation) => {
    conversation.messages.forEach((reply) => {
      if (reply.direction !== 'outbound') threads.push({ inbound: reply, responses: [] });
    });
  });

  const threadsByInboundId = new Map(threads.map((thread) => [thread.inbound.id, thread]));
  conversations.forEach((conversation) => {
    conversation.messages.forEach((reply) => {
      if (reply.direction !== 'outbound') return;
      const parent = reply.inReplyToReplyId ? threadsByInboundId.get(reply.inReplyToReplyId) : null;
      if (parent) parent.responses.push(reply);
      else orphanReplies.push(reply);
    });
  });

  return { threads, orphanReplies };
}

function ReplyMessage({ reply, kind, recipientNameById = {}, orphan = false, children }) {
  const createdAt = toDate(reply.createdAt || reply.receivedAt || reply.sentAt);
  const { body, quoted } = splitReplyBody(plainBody(reply));
  const classNames = [
    'email-reply-message',
    kind === 'outbound' ? 'email-reply-message--outbound' : 'email-reply-message--inbound',
    orphan ? 'email-reply-message--orphan' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames}>
      <div className="email-reply-message__meta">
        <span className="email-reply-message__badge">
          {kind === 'outbound' ? 'Admin reply' : 'Member reply'}
        </span>
        <strong>{replyName(reply, recipientNameById)}</strong>
        <span>{createdAt ? createdAt.toLocaleString() : ''}</span>
      </div>
      {reply.subject && (
        <div className="email-reply-message__subject">{reply.subject}</div>
      )}
      {body && (
        <div className="email-reply-message__body">{body}</div>
      )}
      {quoted && (
        <details className="email-reply-message__quoted">
          <summary>Show quoted history</summary>
          <div>{quoted}</div>
        </details>
      )}
      {kind !== 'outbound' && Array.isArray(reply.attachments) && reply.attachments.length > 0 && (
        <div className="email-reply-message__attachments">
          {reply.attachments.length} attachment{reply.attachments.length === 1 ? '' : 's'} received
        </div>
      )}
      {children}
    </div>
  );
}

export default function EmailReplyThreads({
  replies = [],
  inboundReplies = 0,
  canReply = false,
  teamId = '',
  campaignId = '',
  currentUserId = '',
  recipientNameById = {},
  activeReplyId = '',
  replyBody = '',
  sendingReply = false,
  replyError = '',
  onActiveReplyChange,
  onReplyBodyChange,
  onSendReply,
}) {
  const conversations = useMemo(() => buildReplyConversations(replies), [replies]);
  const storageKey = conversationStorageKey({ teamId, campaignId, currentUserId });
  const allInboundIds = useMemo(() => (
    conversations.flatMap((conversation) => inboundMessages(conversation).map((message) => message.id).filter(Boolean))
  ), [conversations]);
  const inboundSignature = allInboundIds.join('|');
  const [seenInboundIds, setSeenInboundIds] = useState(() => readSeenInboundIds(storageKey).ids);
  const conversationViews = buildConversationViews(conversations, seenInboundIds, recipientNameById);
  const [expandedConversationIds, setExpandedConversationIds] = useState(() => new Set());

  const closeComposer = () => {
    onActiveReplyChange?.('');
    onReplyBodyChange?.('');
  };

  useEffect(() => {
    const { initialized, ids } = readSeenInboundIds(storageKey);
    if (!initialized) {
      const initialSeen = new Set(allInboundIds);
      setSeenInboundIds(initialSeen);
      writeSeenInboundIds(storageKey, allInboundIds);
      return;
    }

    setSeenInboundIds(ids);
    const unreadConversationIds = conversations
      .filter((conversation) => inboundMessages(conversation).some((message) => message.id && !ids.has(message.id)))
      .map((conversation) => conversation.id);

    if (unreadConversationIds.length > 0) {
      setExpandedConversationIds((prev) => new Set([...unreadConversationIds, ...prev]));
      writeSeenInboundIds(storageKey, allInboundIds);
    }
  }, [allInboundIds, conversations, inboundSignature, storageKey]);

  useEffect(() => {
    if (!activeReplyId) return;
    const activeConversation = conversations.find((conversation) => (
      conversation.messages.some((message) => message.id === activeReplyId)
    ));
    if (!activeConversation) return;
    setExpandedConversationIds((prev) => new Set([...prev, activeConversation.id]));
  }, [activeReplyId, conversations]);

  const toggleConversation = (conversationId) => {
    setExpandedConversationIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) next.delete(conversationId);
      else next.add(conversationId);
      return next;
    });
  };

  return (
    <section className="email-reply-thread-card">
      <div className="email-reply-thread-card__header">
        <h3>Replies</h3>
        {inboundReplies > 0 && (
          <span className="email-reply-thread-card__count">
            {inboundReplies} member repl{inboundReplies === 1 ? 'y' : 'ies'}
          </span>
        )}
      </div>

      {replies.length === 0 ? (
        <p className="text-muted email-reply-thread-empty">No replies yet.</p>
      ) : (
        <div className="email-reply-thread-list">
          {conversationViews.map((conversation) => {
            const expanded = expandedConversationIds.has(conversation.id);
            const latestAt = formatTimestamp(conversation.latest?.createdAt || conversation.latest?.receivedAt || conversation.latest?.sentAt);
            return (
              <article className={expanded ? 'email-reply-thread email-reply-thread--open' : 'email-reply-thread'} key={conversation.id}>
                <button
                  type="button"
                  className="email-reply-conversation-summary"
                  onClick={() => toggleConversation(conversation.id)}
                  aria-expanded={expanded}
                >
                  <span className="email-reply-conversation-summary__main">
                    <span className="email-reply-conversation-summary__title">
                      {conversation.title}
                    </span>
                    {!expanded && conversation.preview && (
                      <span className="email-reply-conversation-summary__preview">
                        {conversation.preview}
                      </span>
                    )}
                  </span>
                  <span className="email-reply-conversation-summary__meta">
                    {conversation.unreadCount > 0 && (
                      <span className="email-reply-conversation-summary__unread">
                        {conversation.unreadCount} new
                      </span>
                    )}
                    <span>{conversation.inboundCount} member</span>
                    {conversation.outboundCount > 0 && <span>{conversation.outboundCount} admin</span>}
                    {latestAt && <span>{latestAt}</span>}
                    <span aria-hidden="true">{expanded ? 'Hide' : 'Open'}</span>
                  </span>
                </button>

                {expanded && conversation.messages.map((message) => {
                const createdAt = toDate(message.createdAt || message.receivedAt || message.sentAt);
                const isInbound = message.direction !== 'outbound';
                const composerOpen = isInbound && activeReplyId === message.id;
                const kind = isInbound ? 'inbound' : 'outbound';
                const orphan = message.direction === 'outbound' && !message.inReplyToReplyId;

                return (
                  <React.Fragment key={message.id}>
                    <ReplyMessage reply={message} kind={kind} recipientNameById={recipientNameById} orphan={orphan}>
                      {canReply && isInbound && (
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm email-reply-message__reply-button"
                          onClick={() => {
                            onActiveReplyChange?.(composerOpen ? '' : message.id);
                            onReplyBodyChange?.('');
                          }}
                        >
                          {composerOpen ? 'Close Reply' : 'Reply'}
                        </button>
                      )}
                    </ReplyMessage>

                    {canReply && composerOpen && (
                      <div className="email-reply-composer" aria-label={`Reply to ${replyName(message, recipientNameById)}`}>
                        <div className="email-reply-composer__context">
                          <strong>Replying to {replyName(message, recipientNameById)}</strong>
                          <span>
                            {message.subject || 'No subject'}
                            {createdAt ? ` · ${createdAt.toLocaleString()}` : ''}
                          </span>
                        </div>
                        <label>
                          <span className="form-label">Message</span>
                          <textarea
                            rows={4}
                            value={replyBody}
                            onChange={(event) => onReplyBodyChange?.(event.target.value)}
                            placeholder="Write a reply..."
                          />
                        </label>
                        {replyError && <p className="email-reply-composer__error">{replyError}</p>}
                        <div className="email-reply-composer__actions">
                          <SubmitButton
                            type="button"
                            variant="ghost"
                            onClick={closeComposer}
                            label="Cancel"
                            disabled={sendingReply}
                          />
                          <SubmitButton
                            type="button"
                            variant="primary"
                            onClick={onSendReply}
                            label={sendingReply ? 'Sending...' : 'Send Reply'}
                            disabled={sendingReply || !replyBody.trim()}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
